import { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from './Button';

const CheckIcon = () => <Check className="w-3 h-3 text-white" strokeWidth={3} />;

const OPTIONS_PAGE_SIZE = 30;
// Re-render more options once the scroll position gets this close to the bottom.
const SCROLL_LOAD_THRESHOLD_PX = 48;

export type FilterDropdownValue = string | string[];

export interface FilterDropdownOption {
  value: string;
  label: string;
}

export interface FilterDropdownProps {
  // Name of the filter (e.g. "Status") — shown as the button's own text
  // until something is selected, at which point the button switches to
  // showing the selected option's label(s) instead. Omit for an unlabeled
  // filter chip (falls back to "All" when nothing's selected).
  label?: ReactNode;
  value: FilterDropdownValue;
  onChange: (value: FilterDropdownValue) => void;
  options: FilterDropdownOption[];
  multiple?: boolean;
  // Overrides the default plain-text option row (e.g. add a badge, icon, or
  // secondary text). The checkbox/selected-state wrapper still renders around
  // whatever this returns; only the label content is replaced.
  renderOption?: (option: FilterDropdownOption, state: { selected: boolean }) => ReactNode;
  // Renders `options` incrementally as the panel is scrolled, instead of all
  // at once — for a fully-loaded but large (e.g. hundreds of rows) options
  // array where mounting every option upfront is wasteful. Not real
  // pagination against a backend; `options` must already contain the full
  // list. Panel starts by showing optionsPageSize options and grows by that
  // many each time the scroll nears the bottom.
  infiniteScroll?: boolean;
  optionsPageSize?: number;
}

// Toolbar filter button + option panel (e.g. the Status filter on the
// DataBreaches list). `options` is a { value, label } array — `value` is
// what's stored/compared, `label` is what's shown, so display text can
// differ from the underlying value (e.g. value: "OPEN", label: "Open").
// Distinct from CustomSelect, which is a form-field replacement for
// <select> — this is a toolbar-only filter chip.
//
// The button itself doubles as its own label: it shows the `label` prop
// (e.g. "Status") until something's selected, then swaps to the selected
// option's label(s). No separate caption row — keeps a row of filters
// compact.
//
// Single-select (default): `value` is the selected option's value or '' for
// "All"; picking an option closes the menu.
// Multi-select (`multiple`): `value` is an array of selected values ([] for
// "All"); picking an option toggles a checkbox and keeps the menu open so
// several can be picked in one go.
export const FilterDropdown = ({
  label,
  value,
  onChange,
  options,
  multiple = false,
  renderOption,
  infiniteScroll = false,
  optionsPageSize = OPTIONS_PAGE_SIZE,
}: FilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(optionsPageSize);
  const ref = useRef<HTMLDivElement>(null);

  const selectedValues = multiple && Array.isArray(value) ? value : [];
  const isSelected = (opt: FilterDropdownOption) => (multiple ? selectedValues.includes(opt.value) : value === opt.value);
  const hasSelection = multiple ? selectedValues.length > 0 : !!value;
  const visibleOptions = infiniteScroll ? options.slice(0, visibleCount) : options;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!infiniteScroll) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD_PX) {
      setVisibleCount((c) => Math.min(options.length, c + optionsPageSize));
    }
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const pick = (opt: FilterDropdownOption) => {
    if (!multiple) {
      onChange(opt.value);
      setOpen(false);
      return;
    }
    onChange(
      selectedValues.includes(opt.value)
        ? selectedValues.filter((v) => v !== opt.value)
        : [...selectedValues, opt.value],
    );
  };

  const clear = () => {
    onChange(multiple ? [] : '');
    if (!multiple) setOpen(false);
  };

  const selectedLabel = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  const placeholder = label ?? 'All';
  const buttonText = multiple
    ? (hasSelection ? selectedValues.map(selectedLabel).join(', ') : placeholder)
    : (value ? selectedLabel(value as string) : placeholder);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="tertiary"
        aria-label={typeof label === 'string' ? label : undefined}
        onClick={() => {
          setOpen((o) => !o);
          if (infiniteScroll) setVisibleCount(optionsPageSize);
        }}
        className={`h-9 px-3 justify-start text-sm ${hasSelection ? '!text-[#3F707D] font-medium' : '!text-gray-600 hover:!text-gray-800'}`}
      >
        <span className="min-w-0 truncate max-w-[12rem]">{buttonText}</span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      </Button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-48 w-max max-w-xs max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1"
          onScroll={handleScroll}
        >
          <button
            onClick={clear}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer ${!hasSelection ? 'text-[#3F707D] font-medium' : 'text-gray-700'}`}
          >
            All
          </button>
          {visibleOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => pick(opt)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2 ${isSelected(opt) ? 'text-[#3F707D] font-medium' : 'text-gray-700'}`}
            >
              {multiple && (
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected(opt) ? 'bg-[#3F707D] border-[#3F707D]' : 'border-gray-300'}`}>
                  {isSelected(opt) && <CheckIcon />}
                </span>
              )}
              {renderOption ? (
                <span className="min-w-0 flex-1">{renderOption(opt, { selected: isSelected(opt) })}</span>
              ) : (
                <span className="min-w-0 truncate">{opt.label}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
